import axios from 'axios';
import moment from 'moment';

import {
  VixieesFlowStep,
  VixieesLead,
  VixieesLeadsPage,
} from '../models/VixieesLeadModel';

const API_URL = process.env.REACT_APP_API_URL;
const VIXIEES_URL = `${API_URL}/api/vixiees`;

// Vixiees caps `limit` at 100 and only reaches the newest 1000 matches
// (page * limit <= 1000), with no total count: a short page is the last one.
export const LEADS_PER_PAGE = 100;
export const MAX_PAGES = 10;

export interface ListLeadsParams {
  flowStepId: string;
  page?: number;
  since?: Date;
  until?: Date;
}

export function listFlowSteps(): Promise<{ data: VixieesFlowStep[] }> {
  return axios.get<VixieesFlowStep[]>(`${VIXIEES_URL}/flow-steps/`);
}

export function listLeads({
  flowStepId,
  page = 1,
  since,
  until,
}: ListLeadsParams): Promise<{ data: VixieesLeadsPage }> {
  const params: Record<string, string | number> = {
    flow_step_id: flowStepId,
    page,
    limit: LEADS_PER_PAGE,
  };
  if (since) {
    params.created_at_from = moment(since).startOf('day').toISOString();
  }
  if (until) {
    params.created_at_to = moment(until).endOf('day').toISOString();
  }

  return axios.get<VixieesLeadsPage>(`${VIXIEES_URL}/leads/`, { params });
}

export function isLastPage(page: number, results: VixieesLead[]): boolean {
  return results.length < LEADS_PER_PAGE || page >= MAX_PAGES;
}

// A full last page means the 1000 cap cut the stage short.
export function isCapped(page: number, results: VixieesLead[]): boolean {
  return page >= MAX_PAGES && results.length >= LEADS_PER_PAGE;
}

export async function fetchAllLeads(
  params: Omit<ListLeadsParams, 'page'>
): Promise<{ leads: VixieesLead[]; capped: boolean }> {
  const leads: VixieesLead[] = [];

  for (let page = 1; page <= MAX_PAGES; page++) {
    const { data } = await listLeads({ ...params, page });
    leads.push(...data.results);
    if (isLastPage(page, data.results)) {
      return { leads, capped: isCapped(page, data.results) };
    }
  }

  return { leads, capped: false };
}
